// File: packages/ui/src/ErrorBoundary.tsx
// Version: 2026.4.1 | ForgeTrack error boundary

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '@forgetrack/theme';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ForgeTrack ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={{
          padding: 32,
          textAlign: 'center',
          color: C.coral,
          fontFamily: F.display,
        }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>Something went wrong</div>
          <div style={{ fontSize: 13, color: C.muted, fontFamily: F.mono }}>
            {this.state.error?.message ?? 'Unknown error'}
          </div>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              marginTop: 16,
              padding: '8px 16px',
              background: `${C.coral}20`,
              border: `1px solid ${C.coral}40`,
              borderRadius: 6,
              color: C.coral,
              cursor: 'pointer',
              fontFamily: F.display,
              fontSize: 13,
            }}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
